/**
 * Map redux data to a new array compatible
 * with material-table props.
 * https://stackoverflow.com/questions/65396030/using-material-table-with-redux-state
 * @param data
 */
export const mapTableData = (data) => data.map((item) => Object.assign({}, item));

/**
 * group same data from array
 * @param array
 * @param key
 */
export const groupBy = (array, key) => {
  return array.reduce((result, obj) => {
    (result[obj[key]] = result[obj[key]] || []).push(obj);
    return result;
  }, []);
};

export const delay = (s) =>
  new Promise((r) => {
    setTimeout(() => r(), s * 1000);
  });
export const normalizeKey = (str) => (str || "").toString().toLowerCase().replace(/[\s_-]+/g, "");

export const convertPermissionsToArray = (permissionsObject, existingPermissions = []) => {
  if (!permissionsObject || typeof permissionsObject !== "object") return [];

  const result = [];
  existingPermissions.forEach((perm) => {
    const targetModuleNorm = normalizeKey(perm.module);
    const targetNameNorm = normalizeKey(perm.name);

    for (const [modKey, modVal] of Object.entries(permissionsObject)) {
      if (normalizeKey(modKey) === targetModuleNorm && modVal && typeof modVal === "object") {
        for (const [nameKey, val] of Object.entries(modVal)) {
          if (normalizeKey(nameKey) === targetNameNorm && Boolean(val)) {
            result.push({
              id: perm.id,
              name: perm.name,
              module: perm.module,
              default: true,
            });
          }
        }
      }
    }
  });

  return result;
};

export const formatDeliveryNotes = (deliveryNotes) => {
  if (!deliveryNotes || !Array.isArray(deliveryNotes) || deliveryNotes.length === 0) {
    return { numbers: "", packages: "" };
  }

  const numbers = deliveryNotes
    .map((note) => note?.delivery_note_number || "")
    .filter(Boolean)
    .join(", ");

  const packages = deliveryNotes
    .map((note) => note?.packages || "")
    .filter(Boolean)
    .join(", ");

  return { numbers, packages };
};
export const translatePermission = (t, module, permName) => {
  if (!permName) return "";

  const mod = (module || "").toString().toLowerCase();
  const compositeKey = mod ? `${mod}.${permName}` : permName;

  const translated = t(compositeKey);
  if (translated !== compositeKey) {
    return translated;
  }

  return t(permName);
};

export const getPermissionTitle = (perm, i18n) => {
  if (!perm) return "";
  let title = perm.title;

  if (typeof title === "string") {
    try {
      title = JSON.parse(title);
    } catch (e) {
    }
  }

  let lang = "en";
  let tFunc = null;

  if (typeof i18n === "function") {
    tFunc = i18n;
    lang = i18n.language ? i18n.language.split("-")[0].toLowerCase() : "en";
  } else if (i18n && typeof i18n === "object") {
    if (typeof i18n.t === "function") {
      tFunc = i18n.t.bind(i18n);
    }
    if (i18n.language) {
      lang = i18n.language.split("-")[0].toLowerCase();
    }
  }

  const isEn = lang === "en";
  const isDe = lang === "de";

  const tryTranslate = (key) => {
    if (!tFunc || !key) return null;
    try {
      const res = tFunc(key);
      if (res && typeof res === "string" && res !== key && res !== key.toLowerCase()) {
        return res;
      }
    } catch (e) { }
    return null;
  };

  // 1. If title is a parsed object with localized properties
  if (title && typeof title === "object") {
    const val = isEn
      ? (title.en || title["en-US"] || title["en_US"])
      : (title.de || title["de-DE"] || title["de_DE"] || title[lang]);
    if (typeof val === "string" && val.trim()) {
      return val.trim();
    }
  }

  // 2. Check translation dictionary via i18n.t
  if (perm.name) {
    if (perm.module) {
      const modKey = `${perm.module.toLowerCase()}.${perm.name.toLowerCase()}`;
      const modTrans = tryTranslate(modKey);
      if (modTrans) return modTrans;
    }

    const nameTrans = tryTranslate(perm.name.toLowerCase()) || tryTranslate(perm.name);
    if (nameTrans) return nameTrans;
  }

  // 3. For German: if DB title is a plain string
  if (isDe && typeof title === "string" && title.trim()) {
    return title.trim();
  }

  // 4. For English: format perm.name to Title Case
  if (isEn) {
    if (perm.name) {
      return formatSnakeCaseToTitle(perm.name);
    }
    if (typeof title === "string" && title.trim()) {
      return title.trim();
    }
  }

  // 5. For German fallback: if title object has en, or perm.name
  if (isDe) {
    if (title && typeof title === "object" && title.en && typeof title.en === "string" && title.en.trim()) {
      return title.en.trim();
    }
    if (perm.name) {
      return formatSnakeCaseToTitle(perm.name);
    }
  }

  if (typeof title === "string" && title.trim()) {
    return title.trim();
  }

  return formatSnakeCaseToTitle(perm.name || "");
};

export const getPermissionDescription = (perm, i18n) => {
  if (!perm) return "";
  let desc = perm.description;

  if (typeof desc === "string") {
    try {
      desc = JSON.parse(desc);
    } catch (e) {
    }
  }

  let lang = "en";
  let tFunc = null;

  if (typeof i18n === "function") {
    tFunc = i18n;
    lang = i18n.language ? i18n.language.split("-")[0].toLowerCase() : "en";
  } else if (i18n && typeof i18n === "object") {
    if (typeof i18n.t === "function") {
      tFunc = i18n.t.bind(i18n);
    }
    if (i18n.language) {
      lang = i18n.language.split("-")[0].toLowerCase();
    }
  }

  const isEn = lang === "en";
  const isDe = lang === "de";

  if (desc && typeof desc === "object") {
    const val = isEn
      ? (desc.en || desc["en-US"] || desc["en_US"])
      : (desc.de || desc["de-DE"] || desc["de_DE"] || desc[lang]);
    if (typeof val === "string" && val.trim()) {
      return val.trim();
    }
  }

  if (typeof desc === "string" && desc.trim()) {
    if (tFunc) {
      const trans = tFunc(desc.trim());
      if (trans && trans !== desc.trim()) return trans;
    }
    if (isDe) {
      return desc.trim();
    }
  }

  return "";
};

export const formatSnakeCaseToTitle = (str) => {
  if (!str) return '';
  return str
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');

};

export const getTourDeliveryTimings = (tour) => {
  if (Array.isArray(tour?.delivery_timing_configs) && tour.delivery_timing_configs.length) {
    return [
      ...new Set(
        tour.delivery_timing_configs.map((config) => config.delivery_timing).filter(Boolean)
      ),
    ];
  }
  return Array.isArray(tour?.delivery_timings) ? tour.delivery_timings : [];
};